import React, { useState, useEffect } from "react";
import { 
    Check, 
    X, 
    Eye, 
    FileText, 
    BadgeCheck, 
    Clock, 
    ShieldAlert, 
    ExternalLink, 
    User, 
    Mail, 
    MessageSquare,
    Users,
    ClipboardCheck
} from "lucide-react";
import { CONFIG } from "../../config";
import toast from "react-hot-toast";
import api from "../../api/axios";

export default function ProviderVerification() {
    const [view, setView] = useState("LEADS"); // "LEADS" or "AUDITS"
    const [queue, setQueue] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchQueue();
    }, [view]);

    const fetchQueue = async () => {
        setLoading(true);
        setSelectedItem(null);
        try {
            const endpoint = view === "LEADS" ? "/admin/pending-applications" : "/admin/pending-profiles";
            const res = await api.get(endpoint);
            setQueue(res.data);
            if (res.data.length > 0) setSelectedItem(res.data[0]);
        } catch (err) {
            toast.error(`Failed to load ${view === "LEADS" ? 'applicants' : 'profiles'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleApplicationDecision = async (action) => {
        if (!selectedItem) return;
        
        let confirmMsg = "";
        if (action === "TEA") confirmMsg = "Invite this soul to a Virtual Tea?";
        else if (action === "APPROVE") confirmMsg = "Meeting complete. Grant this Guide entry to the Sanctuary?";
        else confirmMsg = "Archive this application?";

        if (!window.confirm(confirmMsg)) return;

        setProcessing(true);
        try {
            await api.post("/admin/decide-application", {
                applicationId: selectedItem.id,
                action,
                approved: action === "APPROVE" || action === "TEA" // Fail-safe for old backend
            });

            toast.success(action === "TEA" ? "Tea Invitation Sent" : (action === "APPROVE" ? "Access Granted" : "Application Archived"));
            
            if (action === "REJECT") {
                removeFromQueue(selectedItem.id);
            } else {
                // Refresh to show updated status/badge
                fetchQueue();
            }
        } catch (err) {
            toast.error("Process failed");
        } finally {
            setProcessing(false);
        }
    };

    const handleProfileDecision = async (approved) => {
        if (!selectedItem) return;
        const action = approved ? "Verify and Approve Profile?" : "Reject Profile?";
        if (!window.confirm(action)) return;

        setProcessing(true);
        try {
            await api.post(`/admin/verify-profile/${selectedItem.id}`, {
                approved,
                adminNotes: "Admin verified via verification dashboard."
            });

            toast.success(approved ? "Profile Verified & Live" : "Profile Rejected");
            removeFromQueue(selectedItem.id);
        } catch (err) {
            toast.error("Process failed");
        } finally {
            setProcessing(false);
        }
    };

    const removeFromQueue = (id) => {
        const remaining = queue.filter(item => item.id !== id);
        setQueue(remaining);
        setSelectedItem(remaining.length > 0 ? remaining[0] : null);
    };

    if (loading) return (
        <div className="p-20 text-center">
            <div className="w-12 h-12 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Scanning the ether...</p>
        </div>
    );

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col md:flex-row overflow-hidden font-body bg-black text-gray-300">
            
            {/* Sidebar List */}
            <div className={`w-full md:w-80 border-r border-gray-800 flex flex-col bg-[#050505] ${selectedItem ? 'hidden md:flex' : 'flex h-full'}`}>
                
                {/* View Switcher Tabs */}
                <div className="p-2 border-b border-gray-800 flex gap-1 relative">
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full animate-ping opacity-20"></div>
                    <button 
                        onClick={() => setView("LEADS")}
                        className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${view === "LEADS" ? 'bg-brand-blue/10 text-brand-blue border border-brand-blue/20' : 'text-gray-600 hover:text-gray-400'}`}
                    >
                        <Users className="w-3 h-3" /> Leads
                    </button>
                    <button 
                        onClick={() => setView("AUDITS")}
                        className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${view === "AUDITS" ? 'bg-brand-blue/10 text-brand-blue border border-brand-blue/20' : 'text-gray-600 hover:text-gray-400'}`}
                    >
                        <ClipboardCheck className="w-3 h-3" /> Audits
                    </button>
                </div>

                <div className="p-4 border-b border-gray-800 flex justify-between items-center">
                    <h2 className="font-bold text-gray-400 uppercase tracking-widest text-[10px]">
                        {view === "LEADS" ? "Application Queue" : "Profile Audits"} ({queue.length})
                    </h2>
                    <button 
                        onClick={fetchQueue}
                        className="p-1 hover:bg-white/5 rounded transition-colors text-gray-600 hover:text-brand-blue"
                        title="Scan for new souls"
                    >
                        <Clock className="w-3 h-3" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {queue.length === 0 ? (
                        <div className="p-12 text-center">
                            <Check className="w-8 h-8 text-green-500 mx-auto mb-3 opacity-20" />
                            <p className="text-xs text-gray-600 uppercase tracking-widest font-bold">The Queue is Empty</p>
                            <p className="text-[10px] text-gray-700 mt-1 italic">No {view === "LEADS" ? 'new applications' : 'profiles to audit'} found.</p>
                        </div>
                    ) : (
                        queue.map(item => (
                            <div
                                key={item.id}
                                onClick={() => setSelectedItem(item)}
                                className={`p-4 border-b border-gray-800 cursor-pointer hover:bg-white/5 transition-colors ${selectedItem?.id === item.id ? 'bg-white/10 border-l-2 border-l-brand-blue' : ''}`}
                            >
                                <div className="flex justify-between items-start">
                                    <h3 className="text-gray-200 font-medium text-sm">
                                        {view === "LEADS" ? item.name : (item.user?.name || item.name)}
                                    </h3>
                                    {view === "LEADS" && item.status === "TEA_INVITED" && (
                                        <span className="px-2 py-0.5 bg-blue-900/30 text-blue-400 text-[8px] font-bold rounded uppercase tracking-widest border border-blue-900/50">Interviewing</span>
                                    )}
                                </div>
                                <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-1 uppercase tracking-tighter">
                                    <Clock className="w-3 h-3" />
                                    {new Date(item.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Review Area */}
            {selectedItem ? (
                <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden">
                    {/* Left Panel: Basic Details & Media */}
                    <div className="md:w-1/2 p-6 flex flex-col border-r border-gray-800 overflow-y-auto bg-black">
                        
                        <button onClick={() => setSelectedItem(null)} className="md:hidden mb-6 text-gray-500 text-sm">← Back to Queue</button>

                        <div className="bg-gray-900/50 p-6 rounded-2xl border border-gray-800 mb-6 text-center">
                            <h2 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4">Verification Artifact</h2>
                            {view === "LEADS" ? (
                                selectedItem.videoUrl ? (
                                    <a href={selectedItem.videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-blue/10 text-brand-blue rounded-xl hover:bg-brand-blue/20 transition-all font-bold text-sm">
                                        <ExternalLink className="w-4 h-4" /> Open Introduction (Video)
                                    </a>
                                ) : <span className="text-gray-600 italic text-sm">No video provided</span>
                            ) : (
                                selectedItem.certificateFile ? (
                                    <a href={`${import.meta.env.VITE_API_BASE}/uploads/${selectedItem.certificateFile}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-blue/10 text-brand-blue rounded-xl hover:bg-brand-blue/20 transition-all font-bold text-sm">
                                        <FileText className="w-4 h-4" /> View Certificate (PDF/Image)
                                    </a>
                                ) : <span className="text-gray-600 italic text-sm">No certificate uploaded</span>
                            )}
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-800 rounded-2xl flex items-center justify-center text-gray-500 border border-gray-700">
                                    <User className="w-6 h-6" />
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-2xl font-display font-bold text-white truncate">{view === "LEADS" ? selectedItem.name : selectedItem.name}</h1>
                                    <p className="flex items-center gap-2 text-sm text-gray-500 italic">
                                        <Mail className="w-3 h-3" /> {view === "LEADS" ? selectedItem.email : (selectedItem.user?.email || "No email")}
                                    </p>
                                </div>
                            </div>

                            {view === "AUDITS" && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-900 rounded-xl border border-gray-800">
                                        <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Gender</p>
                                        <p className="text-white text-sm font-medium">{selectedItem.gender}</p>
                                    </div>
                                    <div className="p-4 bg-gray-900 rounded-xl border border-gray-800">
                                        <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Aadhaar</p>
                                        <p className="text-white text-sm font-medium">{selectedItem.aadhaar || "Pending"}</p>
                                    </div>
                                </div>
                            )}

                            {view === "AUDITS" && selectedItem.categories && (
                                <div className="p-4 bg-gray-900 rounded-xl border border-gray-800">
                                    <p className="text-[10px] text-gray-500 uppercase font-bold mb-2">Selected Categories</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedItem.categories.map(c => (
                                            <span key={c.categoryId} className="text-[10px] font-bold px-2 py-1 bg-brand-blue/5 text-brand-blue border border-brand-blue/20 rounded-md">
                                                {c.category?.label || c.categoryId}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-auto pt-8 flex gap-4">
                            {view === "LEADS" ? (
                                <>
                                    <button
                                        onClick={() => handleApplicationDecision("REJECT")}
                                        disabled={processing}
                                        className="flex-1 py-4 bg-red-900/10 text-red-500 border border-red-900/30 rounded-2xl hover:bg-red-900/20 transition-all font-bold flex items-center justify-center gap-3 active:scale-95"
                                    >
                                        <X className="w-5 h-5" /> ARCHIVE
                                    </button>
                                    {selectedItem.status === "PENDING" ? (
                                        <button
                                            onClick={() => handleApplicationDecision("TEA")}
                                            disabled={processing}
                                            className="flex-[2] py-4 bg-blue-900/10 text-blue-400 border border-blue-900/30 rounded-2xl hover:bg-blue-900/20 transition-all font-bold flex items-center justify-center gap-3 active:scale-95 shadow-lg shadow-blue-900/10"
                                        >
                                            <MessageSquare className="w-5 h-5" /> INVITE TO VIRTUAL TEA
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleApplicationDecision("APPROVE")}
                                            disabled={processing}
                                            className="flex-[2] py-4 bg-green-900/10 text-green-500 border border-green-900/30 rounded-2xl hover:bg-green-900/20 transition-all font-bold flex items-center justify-center gap-3 active:scale-95 shadow-lg shadow-green-900/10"
                                        >
                                            <BadgeCheck className="w-5 h-5" /> GRANT SANCTUARY ACCESS
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => handleProfileDecision(false)}
                                        disabled={processing}
                                        className="flex-1 py-4 bg-red-900/10 text-red-500 border border-red-900/30 rounded-2xl hover:bg-red-900/20 transition-all font-bold flex items-center justify-center gap-3 active:scale-95"
                                    >
                                        <X className="w-5 h-5" /> REJECT
                                    </button>
                                    <button
                                        onClick={() => handleProfileDecision(true)}
                                        disabled={processing}
                                        className="flex-1 py-4 bg-green-900/10 text-green-500 border border-green-900/30 rounded-2xl hover:bg-green-900/20 transition-all font-bold flex items-center justify-center gap-3 active:scale-95 shadow-lg shadow-green-900/10"
                                    >
                                        <Check className="w-5 h-5" /> APPROVE
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right Panel: Quiz Transcript (For LEADS) or Audit Info (For AUDITS) */}
                    <div className="md:w-1/2 bg-[#0a0a0a] flex flex-col h-full border-t md:border-t-0 border-gray-800">
                        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900">
                            <h2 className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                <MessageSquare className="w-4 h-4 text-brand-blue" />
                                {view === "LEADS" ? "Application Insight" : "Profile Audit Insight"}
                            </h2>
                            {view === "LEADS" && (
                                <div className="px-3 py-1 bg-blue-900/20 text-blue-400 rounded-full text-[10px] font-bold font-mono">
                                    SCORE: {selectedItem.quizScore || "N/A"}
                                </div>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {view === "LEADS" ? (
                                selectedItem.quizData ? (
                                    Object.entries(selectedItem.quizData).map(([qId, answer], idx) => (
                                        <div key={idx} className="space-y-3 p-5 bg-white/5 rounded-2xl border border-white/5 hover:border-brand-blue/20 transition-all">
                                            <p className="text-[10px] font-bold text-brand-blue uppercase tracking-widest leading-none">Perspective {idx + 1}</p>
                                            <p className="text-sm text-gray-400 font-medium leading-relaxed">{qId}</p>
                                            <div className="text-sm text-gray-200 border-l-2 border-brand-blue/30 pl-4 py-1 italic opacity-90 break-words whitespace-pre-wrap">
                                                "{answer}"
                                            </div>
                                        </div>
                                    ))
                                ) : <p className="text-gray-600 text-center italic mt-10 text-sm">No insight data available.</p>
                            ) : (
                                <div className="space-y-6 text-center py-20 opacity-40">
                                    <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-brand-blue" />
                                    <p className="text-sm italic italic max-w-xs mx-auto">Registered guide profile is currently in the manual audit phase. Review the certificate and details carefully before approval.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-600 italic">
                    Select a soul from the queue to review.
                </div>
            )}
        </div>
    );
}
